import { Modal } from 'Modal';
import { memo, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import Button from 'Button';
import { Text } from 'Text';
import { useCurrentUserIguanaObject, useEventLogger } from 'FrontRoyalAngular';
import { InlineLink } from 'InlineLink';
import { useSelector } from 'react-redux';
import { useWeeklyVoiceTimeLimitState } from 'TutorBotConversation';
import './locales/TutorBotVoiceAgent/ShowModalOnVoiceTimeUsedUp-en.json';
import { useSuspenseFeatureGate } from 'FrontRoyalStatsig';
import { useEffectOnce } from 'react-use';
import { getRoomIsOpen } from '../../redux/voiceAgent/voiceAgentSelectors';

/*
    ShowModalOnVoiceTimeUsedUp is a component that sits invisibly on the screen and checks to see if
    the user reaches the weekly limit on voice mode minutes. When that happens, a modal is triggered.

*/

// exported for stories
export const VoiceTimeUsedUpModal: FC<{
    allowedMinutes: number;
    refreshDayOfWeek: string;
    refreshHour: string;
    shouldLogEvent: boolean;
}> = ({ allowedMinutes, refreshDayOfWeek, refreshHour, shouldLogEvent }) => {
    const { t } = useTranslation('back_royal', { keyPrefix: 'TutorBotVoiceAgent.ShowModalOnVoiceTimeUsedUp' });
    const currentUser = useCurrentUserIguanaObject();
    const feedbackEmail = currentUser?.relevantCohort?.supportEmailAddress('feedback') || 'feedback@quantic.edu';
    const EventLogger = useEventLogger();

    // This is temporary. See https://trello.com/c/KQSveuFs
    useEffectOnce(() => {
        if (shouldLogEvent) {
            EventLogger.log('tutorbot:weekly_voice_time_used_up');
        }
    });

    return (
        <Modal title={t('timeLimitReached')}>
            {({ closeModal }) => (
                <div className="px-7 pb-7">
                    <Text>
                        <p>{t('thanksForExploring', { allowedMinutes, refreshDayOfWeek, refreshHour })}</p>
                        <p>
                            {t('weAreRefining', {
                                feedbackEmail: (
                                    <InlineLink href={`mailto:${feedbackEmail}`}>{feedbackEmail}</InlineLink>
                                ),
                            })}
                        </p>
                    </Text>

                    <Button onPress={closeModal} fullWidth color="black">
                        {t('dismiss')}
                    </Button>
                </div>
            )}
        </Modal>
    );
};

const ShowModalOnVoiceTimeUsedUpComponent: FC = () => {
    const { overTimeLimit, refreshDayOfWeek, refreshHour, allowedMinutes } = useWeeklyVoiceTimeLimitState();
    const initialOverTimeLimit = useState(overTimeLimit)[0];
    const shouldLogEvent = useSuspenseFeatureGate('log_weekly_voice_time_used_from_client');
    // We don't want to show the modal while someone is in the voice conversation. We wait until they come out
    const roomIsOpen = useSelector(getRoomIsOpen());

    const modal =
        overTimeLimit && initialOverTimeLimit === false && !roomIsOpen ? (
            <VoiceTimeUsedUpModal
                refreshDayOfWeek={refreshDayOfWeek}
                refreshHour={refreshHour}
                allowedMinutes={allowedMinutes}
                shouldLogEvent={shouldLogEvent}
            />
        ) : null;

    return modal;
};

export const ShowModalOnVoiceTimeUsedUp = memo(
    ShowModalOnVoiceTimeUsedUpComponent,
) as typeof ShowModalOnVoiceTimeUsedUpComponent;
